import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { faPen, faBan, faTrash, faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import { ConfirmDialog } from 'components/UI';
import {
    ExpandableCard,
    ExpandableCardHeader,
    ExpandableCardContent,
    CardTitle,
    CardDescription
} from 'components/UI/V3/ExpandableCard';
import { ThreeDotsDropdownOption } from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdownOption';
import { RateTable } from './RateTable/RateTable';
import styles from './RateCard.module.scss';
import Service from 'model/Service';
import { Rate as BaseRate, ServiceRate } from '@spike/boarding-rule-model';
import { Rate } from '../../Form/RateFormState';
import { useBoardingRates } from '../../BoardingRatesProvider';
import { RootState } from 'store';
import moment from 'moment';

interface RateCardProps {
    boardingService: Service;
    rates?: BaseRate[];
}

export const RateCard: React.FC<RateCardProps> = ({ boardingService }) => {
    const {
        openEditForm,
        deleteRate,
        toggleActive,
        deleteServiceFromRate,
        splitToggleActive,
        isRateSharedWithOtherServices,
        isServiceRatesLoading,
        fetchRatesByServiceId,
        selectedFilter
    } = useBoardingRates();
    const allServiceRates = useSelector((state: RootState) => state.rates.serviceRates);

    // Filter service rates for this service matching the current tab's active status
    const serviceRates = useMemo(() => {
        const showActive = selectedFilter === 'active';
        const now = moment();
        return allServiceRates.filter(sr => {
            if (sr.rateItem.itemId !== boardingService.id) return false;
            const isExpired = now.isAfter(sr.rate.endDate);
            return showActive ? sr.rate.active && !isExpired : !sr.rate.active || isExpired;
        });
    }, [allServiceRates, boardingService.id, selectedFilter]);

    // Get default price for comparison
    const defaultPrice = useMemo(() => {
        return boardingService.pricing?.fixedPrice?.[0]?.price ?? 0;
    }, [boardingService.pricing]);

    const isLoading = isServiceRatesLoading(boardingService.id!);

    const [isExpanded, setIsExpanded] = useState(true);

    // Collapse and block expansion when all rates for this service are gone
    React.useEffect(() => {
        if (!isLoading && serviceRates.length === 0) {
            setIsExpanded(false);
        }
    }, [isLoading, serviceRates.length]);

    const [showConfirm, setShowConfirm] = useState<{
        show: boolean;
        action: 'Activate' | 'Deactivate' | 'Delete';
        rateId?: number;
        serviceRate?: ServiceRate;
    }>({
        show: false,
        action: 'Activate'
    });

    const formatCurrency = (amount?: number): string => {
        if (amount === undefined || amount === null) return '—';
        return `$${amount.toFixed(2)}`;
    };

    const handleCancel = () => {
        setShowConfirm(prev => ({ ...prev, show: false }));
    };

    const handleConfirm = async () => {
        const { action, rateId, serviceRate: sr } = showConfirm;
        const serviceId = boardingService.id!;

        if (action === 'Delete' && sr && rateId) {
            if (isRateSharedWithOtherServices(rateId, sr.rateItem.itemId)) {
                await deleteServiceFromRate(sr);
            } else {
                await deleteRate(rateId);
                await fetchRatesByServiceId(serviceId);
            }
        } else if ((action === 'Activate' || action === 'Deactivate') && sr && rateId) {
            if (isRateSharedWithOtherServices(rateId, sr.rateItem.itemId)) {
                await splitToggleActive(sr, action === 'Activate');
            } else {
                await toggleActive(rateId);
                await fetchRatesByServiceId(serviceId);
            }
        }

        setShowConfirm({ show: false, action: 'Activate' });
    };

    const getActionOptions = (serviceRate: ServiceRate): ThreeDotsDropdownOption[] => {
        const now = moment();
        const isExpired = now.isAfter(serviceRate.rate.endDate);

        const options: ThreeDotsDropdownOption[] = [
            {
                id: 'edit',
                label: 'Edit',
                icon: faPen,
                onClick: () => {
                    const rate = serviceRate.rate as Rate;
                    openEditForm(
                        {
                            ...rate,
                            rateItems: rate.rateItems?.length ? rate.rateItems : [serviceRate.rateItem]
                        },
                        boardingService.id!
                    );
                }
            }
        ];

        if (!isExpired) {
            options.push({
                id: 'toggle',
                label: serviceRate.rate.active ? 'Deactivate' : 'Activate',
                icon: serviceRate.rate.active ? faBan : faCircleCheck,
                onClick: () =>
                    setShowConfirm({
                        show: true,
                        action: serviceRate.rate.active ? 'Deactivate' : 'Activate',
                        rateId: serviceRate.rate.id,
                        serviceRate
                    })
            });
        }

        options.push({
            id: 'delete',
            label: 'Delete',
            icon: faTrash,
            onClick: () =>
                setShowConfirm({
                    show: true,
                    action: 'Delete',
                    rateId: serviceRate.rate.id,
                    serviceRate
                })
        });

        return options;
    };

    const handleExpandedChange = (expanded: boolean) => {
        if (expanded && !isLoading && serviceRates.length === 0) return;
        setIsExpanded(expanded);
    };

    return (
        <>
            <ExpandableCard
                expanded={isExpanded}
                onExpandedChange={handleExpandedChange}
            >
                <ExpandableCardHeader hideExpandIcon={!isLoading && serviceRates.length === 0}>
                    <div className={styles.titleSection}>
                        <CardTitle>{boardingService.name}</CardTitle>
                        <CardDescription>
                            Default Rate{' '}
                            <span className={styles.price}>
                                {formatCurrency(boardingService.pricing?.fixedPrice?.[0]?.price)}/Night
                            </span>
                        </CardDescription>
                    </div>
                </ExpandableCardHeader>

                <ExpandableCardContent className={styles.expandableContent}>
                    <RateTable
                        serviceRates={serviceRates}
                        isLoading={isLoading}
                        defaultPrice={defaultPrice}
                        getActionOptions={getActionOptions}
                    />
                </ExpandableCardContent>
            </ExpandableCard>

            <ConfirmDialog
                open={showConfirm.show}
                title={
                    showConfirm.action === 'Delete'
                        ? 'Delete Rate'
                        : showConfirm.action === 'Activate'
                        ? 'Activate Rate'
                        : 'Deactivate Rate'
                }
                question={
                    showConfirm.action === 'Delete' ? (
                        <>
                            Are you sure you want to delete this rate?
                            <br />
                            This action cannot be undone.
                        </>
                    ) : showConfirm.action === 'Activate' ? (
                        'Are you sure you want to activate this rate?'
                    ) : (
                        'Are you sure you want to deactivate this rate?'
                    )
                }
                cancelButtonLabel="No"
                confirmButtonLabel="Yes"
                onCancel={handleCancel}
                onConfirm={handleConfirm}
            />
        </>
    );
};
